import React, { useEffect, useMemo, useState } from 'react';

import { Button } from './components/Base/Button';
import { useSudoCardPoints } from '../hooks/useSudoCardPoints';
import { useEssential } from './App';
import { Loading } from './components/Base/Loading';
import { useSuiClient } from '@mysten/dapp-kit';
import useTraderOnChainPoints, {
  TraderOnChainPoints,
} from '../hooks/useTraderOnChainPoints';
import { useKioskSelector } from '../hooks/useKioskSelector';
import { useSudoCardSelector } from '../hooks/useSudoCardSelector';
import { KioskSelector } from './components/Kiosk/KioskSelector';
import { useKiosk, useOwnedKiosk } from '../hooks/kiosk';
import { SudoCardSelector } from './components/Kiosk/SudoCardSelector';
import { executeApiCall } from './utils';
import { useKioskClient } from '../context/KioskClientContext';
import { DataAPI } from 'sudo-ts-sdk';
import { formatDateForPoints } from '../utils/utils';
import { OwnedObjectType } from './components/Inventory/OwnedObjects';
import { SudoCardDetails } from './components/Modals/SudoCardDetails';
import { ClaimPointsConfirmation } from './components/Modals/ClaimPointsConfirmation';

const SPoints: React.FC = props => {
  const suiClient = useSuiClient();
  const kioskClient = useKioskClient();
  const [invalid, setInvalid] = useState('');
  const [claimable, setClaimable] = useState<boolean>(true);
  const [kiosk, setKiosk] = useState<string>();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [modalItem, setModalItem] = useState<OwnedObjectType | null>(null);
  const [confirmationModalItem, setConfirmationModalItem] =
    useState<OwnedObjectType | null>(null);

  const { wallet, network, consts, accountData, refreshAccount } =
    useEssential();

  const dataAPI = new DataAPI(network, suiClient);

  const {
    data: ownedKiosk,
    isLoading: isLoadingOwnedKiosk,
    refetch: refetchOwnedKiosk,
  } = useOwnedKiosk(accountData.address);

  const {
    selected: selectedKiosk,
    setSelected: setSelectedKiosk,
    showKioskSelector,
  } = useKioskSelector(accountData.address);

  const {
    selected: selectedSudoCard,
    setSelected: setSelectedSudoCard,
    showSudoCardSelector,
  } = useSudoCardSelector(kiosk);

  const {
    data: kioskData,
    isLoading: isLoadingKioskData,
    isError,
    refetch: refreshKioskData,
  } = useKiosk(kiosk);

  useEffect(() => {
    const nestedSelection = async () => {
      if (!selectedKiosk) return;
      if (selectedKiosk.kioskId !== kiosk) {
        setKiosk(selectedKiosk.kioskId);
      }
    };

    nestedSelection();
  }, [selectedKiosk, kiosk]);

  const {
    data: pointsObjects,
    isLoading,
    refetch: refetchPointsObjects,
  } = useSudoCardPoints({ consts: consts, address: accountData.address });

  const {
    data: onChainPoints,
    isLoading: isOnChainPointsLoading,
    refetch: refetchOnChainPoints,
  } = useTraderOnChainPoints(accountData.address);

  useEffect(() => {
    if (!accountData.address) {
      setInvalid('N/A');
      return;
    }

    setInvalid('');
  }, [accountData.address]);

  const displayPointsObjects = useMemo(() => {
    if (!pointsObjects || !onChainPoints) return [];

    const mappedOnChainPoints = onChainPoints.map(point => {
      point.claimable = false;
      pointsObjects.forEach(pointObject => {
        if (pointObject.objectId === point.objectId) {
          point.claimable = true;
          return;
        }
      });
      point.formattedTimeRange = formatDateForPoints(
        point.startTimeMs,
        point.endTimeMs,
      );
      return point;
    });

    return mappedOnChainPoints.sort((a, b) => {
      if (a === null || b === null) return 0;
      return a.endTimeMs < b.endTimeMs ? 1 : -1;
    });
  }, [pointsObjects, onChainPoints]);

  const claimablePointsAmount = useMemo(() => {
    return displayPointsObjects
      .filter(point => point.claimable)
      .reduce((acc, point) => {
        return acc + point.amount;
      }, 0);
  }, [displayPointsObjects]);

  useEffect(() => {
    if (
      !ownedKiosk?.kioskId ||
      !kioskData ||
      !kioskData.items ||
      kioskData.items.length === 0
    ) {
      setClaimable(false);
    } else {
      setClaimable(true);
    }
  }, [ownedKiosk, kioskData]);

  if (isLoading || isLoadingOwnedKiosk || isLoadingKioskData) {
    return <Loading />;
  }

  const receivePoints = (card: string, points: TraderOnChainPoints[]) => {
    // receive points
    const receivePoints = () =>
      executeApiCall(
        async api => {
          return api.receivePoints(
            kioskClient,
            selectedKiosk!,
            card,
            points.map(point => point.objectId),
          );
        },
        res => {
          const txnUrl = `https://www.suiscan.xyz/${network}/tx/${res?.digest}`;

          dataAPI.updateOnChainPoints(
            points.map(point => point.objectId),
            res?.digest,
          );
          return (
            <a href={txnUrl} target="_blank" rel="noreferrer">
              {' '}
              Success send tx: {res?.digest}{' '}
            </a>
          );
        },
        e => {
          console.error(e);
          return `Error: ${e.message}`;
        },
        wallet,
        network,
        suiClient,
        [
          refreshAccount,
          refetchPointsObjects,
          refetchOnChainPoints,
          refreshKioskData,
        ],
      ).finally(() => setIsSubmitLoading(false));

    // Transfer points to the card
    executeApiCall(
      async api => {
        return api.transferPoints(
          points.map(point => point.objectId),
          card,
        );
      },
      res => {
        // only trigger receive points on success
        receivePoints();
        const txnUrl = `https://www.suiscan.xyz/${network}/tx/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        console.error(e);
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refreshAccount, refetchPointsObjects, refetchOnChainPoints],
    );
  };

  return (
    <div className={`section-container py-5 sm:py-10 sm:px-14 px-5 relative`}>
      <div className="text-3xl !mb-5">"S" Points</div>
      <div className="sm:w-[50%] !mb-10">
        A scrollable history of on-chain points, which you can claim to your S
        Card. Every row contains a point object. On-chain claimable points are
        settled weekly.
      </div>
      <div className="bg-primary/50 border-[1px] border-secondary rounded-lg">
        <div className="flex justify-between items-center !p-6 !pb-4">
          <div className="text-xl font-bold">Reward Detail</div>
          {showKioskSelector && selectedKiosk && ownedKiosk ? (
            <div className="px-4">
              <KioskSelector
                caps={ownedKiosk.caps}
                selected={selectedKiosk}
                setSelected={setSelectedKiosk}
              />
            </div>
          ) : (
            <div className="flex justify-between items-center !p-6 !pb-4">
              You don't have any kiosk.
            </div>
          )}
          {showSudoCardSelector && selectedSudoCard && kioskData ? (
            <div className="px-4">
              <SudoCardSelector
                cards={kioskData.items}
                selected={selectedSudoCard}
                setSelected={card => {
                  setSelectedSudoCard(card);
                  setModalItem(card);
                }}
              />
              {modalItem && (
                <SudoCardDetails
                  item={modalItem}
                  refreshKiosk={refreshKioskData}
                  kioskCap={selectedKiosk!}
                  closeModal={() => setModalItem(null)}
                ></SudoCardDetails>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-center !p-6 !pb-4">
              No S Card in this kiosk.
            </div>
          )}
          {selectedSudoCard ? (
            <div>
              <Button
                loading={isSubmitLoading}
                disabled={
                  !!invalid || !claimable || claimablePointsAmount === 0
                }
                className="app-tab-button app-loader-button bg-green !py-1 "
                onClick={() => {
                  receivePoints(
                    selectedSudoCard.objectId,
                    displayPointsObjects.filter(point => point.claimable),
                  );
                }}
              >
                Claim All
              </Button>
              {confirmationModalItem && (
                <ClaimPointsConfirmation
                  callBackFn={() => {
                    receivePoints(
                      selectedSudoCard.objectId,
                      displayPointsObjects.filter(point => point.claimable),
                    );
                  }}
                  closeModal={() => setConfirmationModalItem(null)}
                ></ClaimPointsConfirmation>
              )}
            </div>
          ) : (
            <div></div>
          )}
        </div>
        <div className="overflow-auto max-h-[calc(100vh-200px)] sm:max-h-[500px] !px-6 !pb-10 relative custom-scrollbar">
          <table className="border-collapse table-auto w-[700px] sm:w-full text-base">
            <thead className="sticky top-0  bg-[rgb(9,12,16)] ">
              <tr>
                <th className="border-b  border-secondary font-medium !py-4 text-slate-400 dark:text-slate-200 text-left">
                  TIME
                </th>
                <th className="border-b  border-secondary font-medium !py-4 text-slate-400 dark:text-slate-200 text-left">
                  POINTS
                </th>
                <th className="border-b  border-secondary font-medium !py-4 text-slate-400 dark:text-slate-200 text-left">
                  CLAIM TX
                </th>
                <th className="border-b  border-secondary font-medium !py-4 text-slate-400 dark:text-slate-200 text-left"></th>
              </tr>
            </thead>
            <tbody>
              {displayPointsObjects?.map((reward, index) => {
                return (
                  <tr key={index}>
                    <td className="border-b border-secondary !py-5">
                      {reward.formattedTimeRange}
                    </td>
                    <td className="border-b border-secondary !py-5">
                      {reward.amount}
                    </td>
                    <td className="border-b border-secondary !py-5">
                      {reward.claimTx ? (
                        <a
                          href={`https://suivision.xyz/txblock/${reward.claimTx}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on Explorer"
                        >
                          <svg
                            className="!w-6 !h-6 fill-white hover:fill-green transition-all duration-700"
                            stroke="currentColor"
                            strokeWidth="0"
                            viewBox="0 0 24 24"
                            height="200px"
                            width="200px"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fill="none" d="M0 0h24v24H0V0z"></path>
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5.5-2.5 7.51-3.49L17.5 6.5 9.99 9.99 6.5 17.5zm5.5-6.6c.61 0 1.1.49 1.1 1.1s-.49 1.1-1.1 1.1-1.1-.49-1.1-1.1.49-1.1 1.1-1.1z"></path>
                          </svg>
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="border-b border-secondary !py-5 flex gap-4 items-center justify-end">
                      {selectedSudoCard ? (
                        reward.claimable ? (
                          <div>
                            <Button
                              loading={isSubmitLoading}
                              disabled={!!invalid || !claimable}
                              className="app-tab-button app-loader-button bg-green !py-1 text-sm font-bold"
                              onClick={() => {
                                receivePoints(selectedSudoCard.objectId, [
                                  reward,
                                ]);
                              }}
                            >
                              Claim
                            </Button>
                            {selectedSudoCard && confirmationModalItem && (
                              <ClaimPointsConfirmation
                                callBackFn={() => {
                                  receivePoints(selectedSudoCard.objectId, [
                                    reward,
                                  ]);
                                }}
                                closeModal={() =>
                                  setConfirmationModalItem(null)
                                }
                              ></ClaimPointsConfirmation>
                            )}
                          </div>
                        ) : (
                          <Button
                            loading={false}
                            disabled={true}
                            className="app-tab-button app-loader-button bg-green !py-1 text-sm font-bold"
                            onClick={() => {}}
                          >
                            N/A
                          </Button>
                        )
                      ) : (
                        <div></div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SPoints;
